/**
 * 일시적 장애(네트워크 끊김, DB 과부하로 인한 timeout/5xx)에 대한 재시도 유틸.
 *
 * 수강신청 오픈 시각처럼 순간 부하가 걸릴 때, 한 번 실패했다고 화면을 비우면
 * 사용자는 "사이트가 죽었다"고 인식한다. 짧은 지터 백오프로 2회까지 다시 시도한다.
 *
 * 주의: 재시도는 부하를 증폭시킬 수 있으므로
 * - 재시도 횟수를 2회로 제한하고
 * - 지터(무작위 지연)를 넣어 동시 재시도가 한 시점에 몰리지 않게 한다.
 * - 4xx(잘못된 요청/권한)는 재시도해도 결과가 같으므로 즉시 포기한다.
 */

const RETRYABLE_STATUS = [408, 429, 500, 502, 503, 504, 522, 524];

function isRetryable(error: any): boolean {
  if (!error) return false;
  // PostgREST/Supabase 에러 형태
  const status = error.status ?? error.code;
  if (typeof status === 'number') return RETRYABLE_STATUS.includes(status);
  // Postgres statement timeout (57014), 연결 관련 코드
  if (typeof status === 'string' && ['57014', '53300', '08006', '08003'].includes(status)) return true;
  // fetch 실패(네트워크 단절)
  const message = String(error.message || '');
  return /fetch|network|timeout|aborted|Failed to fetch/i.test(message);
}

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Supabase 쿼리를 재시도와 함께 실행한다.
 * @param run 매번 새로 쿼리를 만들어 실행하는 함수 (PostgrestBuilder는 1회용이라 함수로 받아야 한다)
 * @param retries 재시도 횟수 (기본 2회 = 최대 3번 시도)
 */
export async function withRetry<T>(
  run: () => PromiseLike<{ data: T | null; error: any }>,
  retries: number = 2
): Promise<{ data: T | null; error: any }> {
  let lastResult: { data: T | null; error: any } = { data: null, error: null };

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      lastResult = await run();
      if (!lastResult.error) return lastResult;
      if (!isRetryable(lastResult.error)) return lastResult;
    } catch (err) {
      lastResult = { data: null, error: err };
      if (!isRetryable(err)) return lastResult;
    }

    if (attempt < retries) {
      // 400ms, 1200ms + 최대 400ms 지터
      const backoff = 400 * Math.pow(3, attempt) + Math.random() * 400;
      await sleep(backoff);
    }
  }

  return lastResult;
}
